import { DEFAULT_CONFIG, PopupConfig, PopupState, PopupStateHandler } from './types';

export class BrowserPopup {
  private window: Window;
  private state: PopupState;
  private error?: Error;
  private intervalId?: number;
  private timestamp: number;
  private onStateChange?: PopupStateHandler;
  private checkState?: PopupStateHandler;
  private messageHandler?: (event: MessageEvent) => void;

  constructor(window: Window, onStateChange?: PopupStateHandler) {
    this.window = window;
    this.state = PopupState.INITIAL;
    this.timestamp = Date.now();
    this.onStateChange = onStateChange;

    // Setup message handler for completion
    this.messageHandler = (event: MessageEvent) => {
      if (event.source === this.window && event.data?.type === PopupState.COMPLETED) {
        this.updateState(PopupState.COMPLETED);
        this.cleanup();
      }
    };
    window.addEventListener('message', this.messageHandler);
  }

  private static calculatePosition(width: number, height: number) {
    // Get the current window's position and dimensions
    const windowLeft = window.screenX || window.screenLeft;
    const windowTop = window.screenY || window.screenTop;
    const windowWidth = window.outerWidth;
    const windowHeight = window.outerHeight;

    // Calculate center position relative to the current window
    const left = windowLeft + Math.max(0, (windowWidth - width) / 2);
    const top = windowTop + Math.max(0, (windowHeight - height) / 2);

    return { left, top };
  }

  private static createWindowFeatures(
    config: Required<PopupConfig>,
    position: { left: number; top: number }
  ): string {
    const features: Record<string, string | number | boolean> = {
      width: config.width,
      height: config.height,
      left: position.left,
      top: position.top,
      resizable: config.resizable,
      scrollbars: config.scrollbars,
    };

    return Object.entries(features)
      .map(([key, value]) => `${key}=${value}`)
      .join(',');
  }

  static create(
    url: string,
    config: Partial<PopupConfig> = {},
    onStateChange?: PopupStateHandler
  ): BrowserPopup {
    const fullConfig = { ...DEFAULT_CONFIG, ...config } as Required<PopupConfig>;
    const position = BrowserPopup.calculatePosition(fullConfig.width, fullConfig.height);

    const popupWindow = window.open(
      url,
      fullConfig.name,
      BrowserPopup.createWindowFeatures(fullConfig, position)
    );

    if (!popupWindow) {
      throw new Error('Failed to open popup window. Please ensure popups are allowed.');
    }

    return new BrowserPopup(popupWindow, onStateChange);
  }

  private updateState(newState: PopupState, error?: Error) {
    this.state = newState;
    this.error = error;
    this.timestamp = Date.now();
    this.onStateChange?.(this.window, newState, error);
  }

  private isBlocked(): boolean {
    return !this.window || this.window.closed === undefined;
  }

  private isClosed(): boolean {
    return !!this.window.closed;
  }

  private isLoading(): boolean {
    try {
      return this.window.document.readyState !== 'complete';
    } catch (e) {
      return true;
    }
  }

  private async checkPopupState() {
    try {
      if (this.isBlocked()) {
        this.updateState(PopupState.BLOCKED);
        this.cleanup();
        return;
      }

      if (this.isClosed()) {
        this.updateState(PopupState.CLOSED);
        this.cleanup();
        return;
      }

      if (this.isLoading()) {
        this.updateState(PopupState.LOADING);
        return;
      }

      if (this.state !== PopupState.READY) {
        this.updateState(PopupState.READY);
        if (this.checkState) {
          await this.checkState(this.window, PopupState.READY);
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        this.updateState(PopupState.ERROR, error);
      }
    }
  }

  startMonitoring(checkState?: PopupStateHandler, interval = 10) {
    this.checkState = checkState;
    this.intervalId = window.setInterval(() => this.checkPopupState(), interval);
  }

  stopMonitoring() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  cleanup() {
    this.stopMonitoring();
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = undefined;
    }
    if (this.window && !this.window.closed) {
      this.window.close();
    }
  }

  getState() {
    return {
      state: this.state,
      error: this.error,
      timestamp: this.timestamp,
    };
  }

  getWindow() {
    return this.window;
  }
}
